import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { getWorkOrderPdfPreflight } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, FileDown, Info } from 'lucide-react';

/**
 * "PDF preflight" modal — sanity-checks what the WO PDF will render
 * BEFORE the admin sends it to a customer. Each section shows a
 * green ✓ / amber ⚠ / red ✗ pill and the value/count that will
 * appear on the PDF. Warnings list the fixable gaps.
 *
 * Read-only server call — safe to poll if the admin wants to fix
 * an issue in another tab and then re-run the check.
 */
const STATUS_META = {
  ok:      { icon: CheckCircle2, className: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
  warn:    { icon: AlertTriangle, className: 'text-amber-700 bg-amber-50 border-amber-100' },
  missing: { icon: XCircle,       className: 'text-slate-500 bg-slate-50 border-slate-100' },
};

const SectionRow = ({ label, section, subtitle }) => {
  const meta = STATUS_META[section.status] || STATUS_META.missing;
  const Icon = meta.icon;
  return (
    <div className={`flex items-start gap-3 p-2.5 rounded border ${meta.className}`} data-testid={`preflight-row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-[10px] uppercase tracking-widest opacity-70">{section.status}</div>
        </div>
        <div className="text-sm font-medium text-slate-900 truncate" title={typeof section.value === 'string' ? section.value : ''}>
          {subtitle}
        </div>
      </div>
    </div>
  );
};

export const PdfPreflightModal = ({ workOrderId, open, onOpenChange, onDownload }) => {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!open || !workOrderId) return;
    let alive = true;
    setState({ loading: true });
    getWorkOrderPdfPreflight(workOrderId)
      .then((d) => alive && setState({ loading: false, data: d }))
      .catch((e) => alive && setState({ loading: false, error: e?.response?.data?.detail || 'Preflight failed' }));
    return () => { alive = false; };
  }, [open, workOrderId]);

  const readinessMeta = state.data && ({
    ready:             { label: 'Ready to send', tone: 'bg-emerald-50 text-emerald-800 border-emerald-100' },
    'ok-with-warnings':{ label: 'OK with warnings', tone: 'bg-amber-50 text-amber-800 border-amber-100' },
    blocked:           { label: 'Missing key info', tone: 'bg-rose-50 text-rose-800 border-rose-100' },
  }[state.data.readiness]);

  const sections = state.data?.sections || {};
  const val = (k) => sections[k]?.value || sections[k]?.name || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pdf-preflight-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" /> PDF Preflight — {workOrderId}
          </DialogTitle>
          <DialogDescription>
            Preview what the WO PDF will render before you send it. Fix anything flagged in amber for a
            polished customer-facing document.
          </DialogDescription>
        </DialogHeader>

        {state.loading && (
          <div className="py-10 text-center text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Inspecting sections…
          </div>
        )}
        {state.error && (
          <div className="py-6 text-center text-rose-700 text-sm">{state.error}</div>
        )}

        {state.data && (
          <div className="space-y-4" data-testid="preflight-body">
            {readinessMeta && (
              <div className={`inline-flex text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded border ${readinessMeta.tone}`} data-testid="preflight-readiness">
                {readinessMeta.label}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <SectionRow label="Customer" section={sections.customer} subtitle={val('customer')} />
              <SectionRow label="Site" section={sections.site} subtitle={
                sections.site?.value
                  ? `${sections.site.value}${sections.site.source === 'customer' ? ' (via customer default)' : ''}`
                  : '— No site'
              } />
              <SectionRow label="Asset" section={sections.asset} subtitle={val('asset')} />
              <SectionRow label="Assignee" section={sections.assignee} subtitle={
                sections.assignee?.value
                  ? `${sections.assignee.value}${sections.assignee.title ? ` · ${sections.assignee.title}` : ''}`
                  : '—'
              } />
              <SectionRow label="Description" section={sections.description} subtitle={sections.description?.value || '—'} />
              <SectionRow label="Hours" section={sections.hours} subtitle={
                sections.hours?.target
                  ? `${sections.hours.logged}h logged · target ${sections.hours.target}h`
                  : sections.hours?.logged
                    ? `${sections.hours.logged}h logged · no target set`
                    : 'No hours logged'
              } />
              <SectionRow label="Parts" section={sections.parts} subtitle={
                sections.parts?.count
                  ? `${sections.parts.count} row${sections.parts.count === 1 ? '' : 's'}` +
                    (sections.parts?.missingNameSkus?.length
                      ? ` · missing names for ${sections.parts.missingNameSkus.length} SKU(s)`
                      : '')
                  : 'None'
              } />
              <SectionRow label="Attachments" section={sections.attachments} subtitle={
                sections.attachments?.count ? `${sections.attachments.count} file${sections.attachments.count === 1 ? '' : 's'}` : 'None'
              } />
              <SectionRow label="Notes" section={sections.notes} subtitle={
                sections.notes?.count ? `${sections.notes.count} update${sections.notes.count === 1 ? '' : 's'}` : 'None'
              } />
              <SectionRow label="RAMS" section={sections.rams} subtitle={
                sections.rams?.count ? `${sections.rams.count} document${sections.rams.count === 1 ? '' : 's'} (appended)` : 'Not attached'
              } />
              <SectionRow label="Sign-off" section={sections.signOff} subtitle={
                sections.signOff?.name
                  ? `${sections.signOff.name}${sections.signOff.role ? ` · ${sections.signOff.role}` : ''}`
                  : 'Not yet signed'
              } />
              <SectionRow label="Letterhead" section={sections.letterhead} subtitle={
                sections.letterhead?.status === 'ok'
                  ? 'Branded (template active)'
                  : sections.letterhead?.templateActive
                    ? 'Template exists but no letterhead PDF'
                    : 'No work_order template configured'
              } />
            </div>

            {state.data.warnings?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3" data-testid="preflight-warnings">
                <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Warnings ({state.data.warnings.length})
                </div>
                <ul className="text-sm text-amber-900 space-y-1 list-disc pl-5">
                  {state.data.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="preflight-close-btn">
            Close
          </Button>
          {onDownload && state.data && (
            <Button
              onClick={() => { onOpenChange(false); onDownload(); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="preflight-download-btn"
            >
              <FileDown className="h-4 w-4 mr-1.5" />
              {state.data.readiness === 'blocked' ? 'Download anyway' : 'Download PDF'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfPreflightModal;
